// VideoStoryUtils.js - 提取的工具函数

import React from 'react';
import { BookOpen } from "lucide-react";


// 格式化时间 (MM:SS)
export const formatTime = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes.toString().padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
};

// 事件类型标签映射 - 映射事件类型到情感标签
export const eventLabels = {
  1: "Civil-Vehicle",
  2: "Civil-Person",
  3: "Civil-Other",
  4: "Crime-Vehicle",
  5: "Crime-Person",
  6: "Crime-Other",
};

// 获取事件图标
export const getEventIcon = (type) => {
  const typeNum = Number(type);
  switch(typeNum) {
    case 1: return <img src="/smallicons/important_car_small.png" alt="Civil-Vehicle" width="18" height="18" />;
    case 2: return <img src="/smallicons/important_people_small.png" alt="Civil-Person" width="18" height="18" />;
    case 3: return <img src="/smallicons/important_others_small.png" alt="Civil-Other" width="18" height="18" />;
    case 4: return <img src="/smallicons/urgent_car_small.png" alt="Crime-Vehicle" width="18" height="18" />;
    case 5: return <img src="/smallicons/urgent_people_small.png" alt="Crime-Person" width="18" height="18" />;
    case 6: return <img src="/smallicons/urgent_others_small.png" alt="Crime-Other" width="18" height="18" />;
    default: return <BookOpen size={18} />;
  }
};

// 优先级顺序 - 优先级排序：Emergency, Urgent, Moderate, Advisory
export const priorityOrder = [1, 2, 3, 4];

// 获取优先级标签函数
export const getPriorityLabel = (priority) => {
  const priorityNum = Number(priority);
  switch(priorityNum) {
    case 1: return "Emergency";  // 原来是 "Critical"
    case 2: return "Urgent";     // 原来是 "Important"
    case 3: return "Moderate";   // 原来是 "Routine"
    case 4: return "Advisory";   // 原来是 "Optional"
    default: return "Unclassified";
  }
};

// 获取优先级颜色函数
export const getPriorityColor = (priority) => {
  const priorityNum = Number(priority);
  switch(priorityNum) {
    case 1: return "#bd0026"; // 红色 - Critical
    case 2: return "#f03b20"; // 橙色 - Important
    case 3: return "#fd8d3c"; // 蓝色 - Routine
    case 4: return "#90EE90"; // 灰色 - Optional
    default: return "#9CA3AF";
  }
};

// 创建实际时间戳
export const createStoryTimeStamp = (timeframe, startTime) => {
  // 基于timeframe确定是白天还是夜晚
  const isDaytime = timeframe === "11AM";
  
  // 从视频时间计算分钟和秒
  const minutes = Math.floor(startTime / 60);
  const seconds = Math.floor(startTime % 60);
  
  // 设置基础时间
  let hour = isDaytime ? 11 : 23; // 白天为11AM，夜晚为11PM
  let finalMinutes = minutes;
  
  // 处理分钟溢出
  hour += Math.floor(finalMinutes / 60);
  finalMinutes = finalMinutes % 60;
  
  // 格式化时间
  const formattedHour = hour > 12 ? `${hour - 12}` : `${hour}`;
  const ampm = hour >= 12 ? "PM" : "AM";
  const formattedMinutes = finalMinutes.toString().padStart(2, '0');
  const formattedSeconds = seconds.toString().padStart(2, '0');
  
  // 返回格式化时间
  return `${formattedHour}:${formattedMinutes}:${formattedSeconds} ${ampm}`;
};

// 获取故事风格事件描述
export const getStoryDescription = (event, robotId) => {
  if (event.summary) return event.summary;
  
  // 基于事件类型生成故事风格描述
  const eventType = Number(event.type) || 1;
  const confidence = event.confidence || "medium";
  
  // 基于事件类型和置信度创建动态描述
  const baseDescriptions = [
    "Robot detected a noteworthy situation.", 
    "This is an interesting observation.",
    "This activity was detected during monitoring.",
    "This situation needs further attention."
  ];
  
  const confidenceText = {
    "high": "with high confidence",
    "medium": "with moderate confidence",
    "low": "tentatively"
  };
  
  // 创建情感描述
  return `Robot ${robotId} ${confidenceText[confidence]} recorded this scene during patrol. ${baseDescriptions[eventType % baseDescriptions.length]}`;
};

// 检查事件是否有边界框数据
export const hasBboxes = (event) => {
  return event && event.bboxes && Array.isArray(event.bboxes) && event.bboxes.length > 0;
};

// 生成动态故事标题
export const generateStoryTitle = (robotId) => {
  return `Robot ${robotId}'s Video Debrief`;
};

// 对事件进行排序
export const sortEvents = (events, sortMethod) => {
  if (!events || !events.length) return [];
  
  return [...events].sort((a, b) => {
    if (sortMethod === 'priority') {
      // 优先级优先排序（较低数字=较高优先级）
      if ((a.priority || 3) !== (b.priority || 3)) {
        return (a.priority || 3) - (b.priority || 3);
      }
      // 同一优先级按时间排序
      return a.startTime - b.startTime;
    } else {
      // 纯时间排序
      return a.startTime - b.startTime;
    }
  });
};

// 准备分组事件对象 - 用于导航组件
export const prepareGroupedEvents = (events) => {
  const result = {};
  
  events.forEach((event, index) => {
    const priority = event.priority || 3;
    if (!result[priority]) {
      result[priority] = [];
    }
    // 确保保存原始索引
    result[priority].push({
      ...event,
      originalIndex: event.originalIndex !== undefined ? event.originalIndex : index
    });
  });
  
  return result;
};